import { ApiError } from './api';

export interface UserErrorOptions {
    /** Lower-case verb phrase, e.g. "refresh source" or "schedule recording". */
    action?: string;
    fallback?: string;
}

const CODE_MESSAGES: Record<string, string> = {
    bad_url: 'The stream URL is invalid.',
    cannot_edit_status: 'That recording has already started or finished.',
    forbidden: 'You do not have permission to do that.',
    insufficient_storage: 'Not enough disk space. Free space or delete old recordings, then try again.',
    invalid_credentials: 'The provider rejected the saved credentials.',
    max_connections: 'The provider rejected the request, likely because the account is already at its stream limit.',
    not_found: 'That item no longer exists.',
    not_recording: "That recording isn't currently active.",
    rewind_channel_disabled: 'Rewind is not enabled for this channel.',
    schedule_conflict: 'That programme is already scheduled. Check Recordings for the existing row.',
    source_not_found: 'That source no longer exists.',
    transcode_probe_timeout: 'The media probe timed out.',
    unauthorized: 'Your session expired. Sign in again.',
    unsupported_video_codec: 'This stream uses a video codec this browser cannot play. Open it in an external player.',
    upstream_unreachable: 'The provider could not be reached.',
    use_stop_first: 'This recording is still running — use Stop recording first.',
    video_transcode_not_allowed: 'Video transcoding is disabled for playback. Atlas can only repair audio.',
    probe_failed: 'Atlas could not inspect this stream safely before playback.',
    // X1 (ux-remediation D13) — recording-scoped transcode-session codes
    // (POST /api/transcode/session with {recordingId}).
    not_finished: "This recording hasn't finished yet — try again once it's done.",
    // "isn't available locally", not "is gone": cloud-offloaded recordings
    // (dvrRetention nulls `path` after rclone upload) hit this too — the
    // file exists remotely, the transcode path just can't reach it yet
    // (follow-up noted in docs/plans/ux-remediation.md).
    file_missing: "This recording's file isn't available for playback right now.",
};

/**
 * X2 (ux-remediation D3/D4) — errorKind → user-message vocabulary. ONE
 * canonical resolver for every layer that phrases the SAME server-reported
 * fact (root CLAUDE.md doctrine #2): the honest `stream_limit` phrasing used
 * to live only in `lib/channelMenuActions.ts`, while the VOD path
 * (`useVodStreamUrl.ts` / `Watch.vue`) hid the identical cause behind generic
 * probe-failure copy (audit D4). Server vocabulary surveyed across
 * `server/services/probeService.js` (VOD probe `errorKind`) and
 * `server/services/livePreflight.js` / `streamLimit.js` (live pre-flight
 * `outcome`): `stream_limit` and `channel_offline` are the two kinds either
 * layer emits today. Unrecognised / null / undefined kinds return null so
 * callers keep their own generic fallback copy.
 */
const ERROR_KIND_MESSAGES: Record<string, string> = {
    stream_limit:
        'Stream limit reached — your provider rejected the quality check. Wait a moment, then try again. The channel was NOT marked offline.',
    channel_offline: 'This channel appears to be offline — the provider is serving a placeholder instead of a live stream.',
};

export function playbackErrorKindMessage(errorKind: string | null | undefined): string | null {
    if (!errorKind) return null;
    return ERROR_KIND_MESSAGES[errorKind] ?? null;
}

function bodyMessage(body: unknown): string | null {
    if (!body || typeof body !== 'object') return null;
    const maybeMessage = (body as { message?: unknown }).message;
    if (typeof maybeMessage === 'string' && maybeMessage.trim()) return maybeMessage.trim();
    return null;
}

function humaniseCode(code: string): string {
    const words = code.replace(/[_-]+/g, ' ').trim();
    if (!words) return 'The request failed.';
    return words[0]!.toUpperCase() + words.slice(1) + '.';
}

function apiReason(err: ApiError): string {
    const mapped = err.code ? CODE_MESSAGES[err.code] : undefined;
    if (mapped) return mapped;
    const serverMessage = bodyMessage(err.body);
    if (serverMessage) return serverMessage;

    switch (err.status) {
        case 400:
            return 'The request was rejected. Check the form values and try again.';
        case 401:
            return CODE_MESSAGES.unauthorized ?? 'Your session expired. Sign in again.';
        case 403:
            return CODE_MESSAGES.forbidden ?? 'You do not have permission to do that.';
        case 404:
            return CODE_MESSAGES.not_found ?? 'That item no longer exists.';
        case 409:
            return 'The request conflicts with an existing item.';
        case 429:
            return 'Too many requests. Wait a moment and try again.';
        case 502:
        case 504:
            return 'The provider or transcoder did not respond.';
        default:
            if (err.status >= 500) return 'Atlas hit a server error. Try again; check logs if it keeps failing.';
            return err.code ? humaniseCode(err.code) : err.message || 'The request failed.';
    }
}

function networkReason(err: Error): string {
    if (err.name === 'AbortError') return 'The request was cancelled.';
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return 'This browser appears to be offline.';
    }
    return err.message || 'Network request failed.';
}

/**
 * Fallback wording per occupant kind, for a server too old to send `message`.
 * Only kinds whose wording must DIFFER need an entry — the default already
 * describes every occupant the user can end themselves.
 */
const SLOT_BUSY_BY_KIND: Readonly<Record<string, string>> = Object.freeze({
    recording:
        'This source is recording another channel. Recordings keep the connection until they finish.',
});

const SLOT_BUSY_DEFAULT = 'This source is already streaming another channel.';

/**
 * THE source-slot-conflict message. One resolver, because the distinction it
 * carries is the whole point and two surfaces used to throw it away.
 *
 * The server already separates "another channel is streaming" from "a
 * recording holds the connection until it finishes" (`busyMessageFor` in
 * liveBufferService.js). That is not cosmetic: one of those the user can end,
 * and the other they cannot — so `Live.vue`'s and `channelMenuActions`'s
 * hard-coded "Source slot occupied by X. Stop that stream first, then try
 * again." told someone to stop a recording, on a screen with no way to stop
 * it, for a connection that was never going to be released early.
 *
 * The server's own sentence wins whenever it is present, so a NEW occupant
 * kind needs no change here — the local table is only the pre-`message`
 * fallback. The channel label is appended rather than interpolated for the
 * same reason: the sentence belongs to whoever knows the occupant.
 */
export function sourceSlotBusyMessage(body: unknown, channelLabel?: string | null): string {
    const base = bodyMessage(body) ?? SLOT_BUSY_BY_KIND[sourceSlotOccupantKind(body) ?? ''] ?? SLOT_BUSY_DEFAULT;
    const label = typeof channelLabel === 'string' ? channelLabel.trim() : '';
    return label ? `${base} Currently: ${label}.` : base;
}

/**
 * Which kind of occupant holds the slot (`buffer` | `transcode` | `proxy` |
 * `remux` | `probe` | `recording`), or null when the server did not say.
 *
 * Read it to decide whether an AFFORDANCE makes sense, never to build the
 * message — that is `sourceSlotBusyMessage`'s job.
 */
export function sourceSlotOccupantKind(body: unknown): string | null {
    if (!body || typeof body !== 'object') return null;
    const kind = (body as { occupyingKind?: unknown }).occupyingKind;
    return typeof kind === 'string' && kind.length > 0 ? kind : null;
}

/**
 * The source-slot conflict copy for a PERSISTENT surface — the inline panel on
 * Live's full composition, and the sheet on the compact one.
 *
 * Separate from `sourceSlotBusyMessage` (which writes the transient toast) and
 * canonical for the same reason that one is: the recording distinction is the
 * whole point, and a surface that drops it tells the user to do something they
 * cannot. It lives here rather than in `Live.vue` because it had ALREADY
 * drifted — `Live.vue` computed both strings inline while
 * `LiveActionsSheet.vue` hardcoded the non-recording wording, so on the
 * compact composition (phones, and the Fold in both postures) a recording
 * holding the slot read "Slot occupied by X" over a button offering to "Join
 * current buffered channel". Neither surface was wrong on its own; the
 * projection between them (`sheetSlotBusy`) simply never carried
 * `occupyingKind`.
 */
export function slotBusyPanelLabel(occupyingKind: string | null | undefined, channelLabel: string): string {
    return occupyingKind === 'recording'
        ? `Recording ${channelLabel} — the connection is held until it finishes`
        : `Slot occupied by ${channelLabel}`;
}

/**
 * The label on the action beside that copy.
 *
 * A recording cannot be joined the way a viewer's buffer can, and the button
 * that says so must not say "Join" — the user would be agreeing to something
 * the server will refuse. "Watch that channel" is what the action actually
 * does: tune to the channel the recording is already pulling.
 */
export function slotBusyJoinLabel(occupyingKind: string | null | undefined): string {
    return occupyingKind === 'recording' ? 'Watch that channel' : 'Join current buffered channel';
}

export function formatUserError(err: unknown, opts: UserErrorOptions = {}): string {
    const reason =
        err instanceof ApiError ? apiReason(err) : err instanceof Error ? networkReason(err) : opts.fallback ?? 'Something went wrong.';
    if (!opts.action) return reason;
    return `Could not ${opts.action}. ${reason}`;
}

/**
 * X1 (ux-remediation D13) — dedicated formatter for a failed
 * recording-transcode-session attempt (`POST /api/transcode/session` with
 * `{recordingId}`). `apiReason()`'s generic 400 fallback ("The request was
 * rejected. Check the form values and try again.") is page-load
 * form-validation phrasing that makes no sense on a screen the user reached
 * by clicking Play on a recording — the exact defect the audit caught
 * (D2's cascade). This recognises the contract's specific codes
 * (`not_finished`, `file_missing`, the shared `not_found` for an
 * unknown/foreign row) and otherwise returns an honest, recording-specific
 * generic message instead of `apiReason()`'s per-status default.
 */
export function formatRecordingTranscodeError(err: unknown): string {
    const fallback = 'Could not prepare this recording for playback.';
    if (err instanceof ApiError) {
        const code = err.code;
        if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];
        return bodyMessage(err.body) ?? fallback;
    }
    if (err instanceof Error) return networkReason(err);
    return fallback;
}

export function errorMessageForLog(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}
